import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/hooks/useReveal";
import { publicApi } from "@/lib/api";
import { useBranding } from "@/contexts/BrandingContext";

const PlayerGrid = ({ players }) => {
    if (!players?.length) {
        return (
            <div className="bg-liv-card border border-liv-border p-10 text-center text-neutral-400" data-testid="team-empty-state">
                Bu kategoride henüz oyuncu eklenmemiş.
            </div>
        );
    }
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {players.map((p) => (
                <Link key={p.id} to={`/oyuncular/${p.slug}`} className="group bg-liv-card border border-liv-border hover:border-liv-yellow" data-testid={`team-player-card-${p.slug}`}>
                    <div className="aspect-[3/4] relative overflow-hidden bg-black">
                        {p.photo_url ? (
                            <img src={p.photo_url} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-neutral-700 font-display text-4xl">#{p.jersey_number || "?"}</div>
                        )}
                        {p.jersey_number != null && <div className="absolute top-2 right-2 font-display text-2xl text-liv-yellow drop-shadow">#{p.jersey_number}</div>}
                    </div>
                    <div className="p-2">
                        <div className="text-[10px] uppercase text-liv-yellow">{p.position || "—"}</div>
                        <div className="font-semibold text-sm leading-tight">{p.name}</div>
                    </div>
                </Link>
            ))}
        </div>
    );
};

const Team = () => {
    const { branding } = useBranding();
    const [players, setPlayers] = useState([]);
    const [staff, setStaff] = useState([]);
    const [next, setNext] = useState(null);
    const [tab, setTab] = useState("a_team");

    useEffect(() => {
        Promise.all([
            publicApi.players(),
            publicApi.staff({ category: "a-team" }),
            publicApi.nextMatch(),
        ]).then(([p, s, n]) => { setPlayers(p || []); setStaff(s || []); setNext(n); });
    }, []);

    const aTeam = players.filter((p) => (p.team_type || "a_team") === "a_team");
    const altyapi = players.filter((p) => p.team_type === "altyapi");
    const clubName = branding?.short_name || branding?.name || "Kulüp";

    return (
        <PublicLayout>
            <SEO title={`Takım · ${clubName}`} description={`${clubName} A Takım ve Altyapı kadrosu, teknik ekip ve fikstür özeti.`} />
            <section className="relative h-[55vh] md:h-[70vh] bg-black overflow-hidden">
                <img src="https://images.pexels.com/photos/12616082/pexels-photo-12616082.jpeg" alt="Takım" className="absolute inset-0 w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative container-x h-full flex flex-col justify-end pb-12">
                    <div className="overline mb-2">Kulüp</div>
                    <h1 className="font-display text-6xl md:text-9xl uppercase">Takım</h1>
                    <p className="mt-4 max-w-2xl text-neutral-300">A Takım kadromuz, altyapı oyuncularımız, teknik ekibimiz ve yaklaşan maçlarımız.</p>
                </div>
            </section>

            <div className="container-x section-pad">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16">
                    <div className="bg-liv-card border border-liv-border p-8">
                        <div className="overline">A Takım</div>
                        <div className="font-display text-7xl text-liv-yellow mt-2">{aTeam.length}</div>
                        <div className="text-sm text-neutral-400 mt-1">Aktif Oyuncu</div>
                    </div>
                    <div className="bg-liv-card border border-liv-border p-8">
                        <div className="overline">Altyapı</div>
                        <div className="font-display text-7xl text-liv-yellow mt-2">{altyapi.length}</div>
                        <div className="text-sm text-neutral-400 mt-1">Yetiştirme Kadrosu</div>
                    </div>
                    <div className="bg-liv-yellow text-black p-8">
                        <div className="text-xs font-bold uppercase tracking-[0.3em]">Sıradaki Maç</div>
                        {next?.id ? (
                            <>
                                <div className="font-display text-2xl sm:text-3xl mt-2 leading-tight break-words">{next.opponent}</div>
                                <div className="text-sm font-semibold mt-1">{new Date(next.match_date).toLocaleString("tr-TR")}</div>
                                <Link to="/mac-merkezi" className="mt-4 inline-block underline font-bold text-sm">Detay →</Link>
                            </>
                        ) : <div className="font-display text-2xl mt-2">Yakında açıklanacak</div>}
                    </div>
                </div>

                <Reveal>
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                        <h2 className="font-display text-5xl uppercase">Kadromuz</h2>
                        <div className="inline-flex bg-liv-card border border-liv-border" data-testid="team-tabs">
                            <button
                                onClick={() => setTab("a_team")}
                                data-testid="team-tab-a-team"
                                className={`px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-colors ${tab === "a_team" ? "bg-liv-yellow text-black" : "text-white/70 hover:text-white"}`}
                            >
                                A Takım ({aTeam.length})
                            </button>
                            <button
                                onClick={() => setTab("altyapi")}
                                data-testid="team-tab-altyapi"
                                className={`px-5 py-2.5 text-xs uppercase tracking-widest font-bold transition-colors ${tab === "altyapi" ? "bg-liv-yellow text-black" : "text-white/70 hover:text-white"}`}
                            >
                                Altyapı ({altyapi.length})
                            </button>
                        </div>
                    </div>
                </Reveal>

                <PlayerGrid players={tab === "a_team" ? aTeam : altyapi} />

                {staff.length > 0 && (
                    <div className="mt-16">
                        <Reveal>
                            <h2 className="font-display text-3xl uppercase mb-6">Teknik Ekip</h2>
                        </Reveal>
                        <div className="text-sm text-neutral-400">
                            {staff.length} kişilik teknik ekip — <Link to="/teknik-ekip" className="text-liv-yellow hover:underline">tamamına git →</Link>
                        </div>
                    </div>
                )}
            </div>
        </PublicLayout>
    );
};

export default Team;
